import { useNavigate } from "@tanstack/react-router";
import { InfoCircle, IntersectCircle } from "@untitledui/icons";
import type { TargetCollaborationGroupedItem, TargetCollaborationMetric } from "@/api/collaborations";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { SEEN_ROW_CLASS, useSeenRows } from "@/components/application/table/use-seen-rows";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { ExpiryStatusCell } from "@/pages/collaborations/expiry-status-cell";
import { GmvCell } from "@/pages/collaborations/gmv-cell";
import { ModifiedCell } from "@/pages/collaborations/modified-cell";
import { OverdueCell } from "@/pages/collaborations/overdue-cell";
import { ProductChips } from "@/pages/collaborations/product-chips";
import {
  MetricSkeleton,
  resolveMetric,
  seenGroupId,
  TARGET_COLLABORATIONS_TABLE_KEY,
} from "@/pages/collaborations/target-collaborations-table";
import { useMerchant } from "@/providers/MerchantProvider";
import { cx } from "@/utils/cx";

const numberFormatter = new Intl.NumberFormat("en-US");

export function UngroupedCollabsTable({
  items,
  metrics,
  isLoading,
}: {
  items: TargetCollaborationGroupedItem[];
  metrics?: Record<string, TargetCollaborationMetric>;
  isLoading?: boolean;
}) {
  const navigate = useNavigate();
  const { isSeen, markSeen } = useSeenRows(TARGET_COLLABORATIONS_TABLE_KEY);
  const merchant = useMerchant();

  return (
    <div className="overflow-x-auto rounded-lg border border-secondary">
      <div className="min-w-max">
        <div className="flex items-center border-b border-secondary bg-primary">
          <div className="w-[477px] shrink-0 rounded-tl-md pl-5 py-2.5 text-xs font-semibold text-quaternary">Name</div>
          <div className="flex-1 px-[20px] py-[12px] text-xs font-semibold text-quaternary">Total GMV</div>
          <div className="flex-1 px-[20px] py-[12px] text-xs font-semibold text-quaternary">Modified</div>
          <div className="flex-1 px-[20px] py-[12px] text-xs font-semibold text-quaternary">Expiry Status</div>
          <div className="flex-1 px-[20px] py-[12px] text-xs font-semibold text-quaternary">Invite pending</div>
          <div className="flex-1 px-[20px] py-[12px] text-xs font-semibold text-quaternary">Sample requests</div>
          <div className="flex-1 px-[20px] py-[12px] text-xs font-semibold text-quaternary">Post Overdue</div>
          <div className="flex-1 rounded-tr-md px-[20px] py-[12px] text-xs font-semibold text-quaternary">Videos</div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-6 text-tertiary">
            <LoadingIndicator size="sm" />
            <span className="text-xs">Loading collaborations…</span>
          </div>
        ) : (
          items.map((item) => {
            const isSmartUnboxGroup = item.isGroupHeader && item.groupSize > 1;
            const metricKey = item.groupId && item.isGroupHeader ? `group-${item.groupId}` : item.collaborationId;
            const metric = metrics?.[metricKey];
            const invitePendingCount = resolveMetric(item.invitePendingCount, metric, "invitePendingCount");
            const sampleRequestedCount = resolveMetric(item.sampleRequestedCount, metric, "sampleRequestedCount");
            const postOverdueCount = resolveMetric(item.postOverdueCount, metric, "postOverdueCount");
            const postedVideosCount = resolveMetric(item.postedVideosCount, metric, "postedVideosCount");
            const seenId = isSmartUnboxGroup && item.groupId ? seenGroupId(item.groupId) : item.collaborationId;
            const handleRowClick = () => {
              markSeen(seenId);
              if (isSmartUnboxGroup && item.groupId) {
                navigate({
                  to: "/merchant/$merchantId/collaborations/target-group/$groupId",
                  params: { merchantId: merchant, groupId: item.groupId },
                });
              } else {
                navigate({
                  to: "/merchant/$merchantId/collaborations/target/$collaborationId",
                  params: { merchantId: merchant, collaborationId: item.collaborationId },
                });
              }
            };
            return (
              <div
                key={item.collaborationId}
                role="button"
                tabIndex={0}
                onClick={handleRowClick}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") handleRowClick();
                }}
                className="flex h-[72px] cursor-pointer items-center border-b border-secondary bg-primary last:rounded-b-md last:border-b-0 hover:bg-secondary"
              >
                <div
                  className={cx(
                    "flex w-[477px] shrink-0 flex-col justify-center gap-1 pl-5 pr-5",
                    isSeen(seenId) && SEEN_ROW_CLASS,
                  )}
                >
                  <div className="flex items-center gap-3">
                    <span className="truncate text-sm font-medium text-primary">{item.name}</span>
                    <Tooltip
                      title="Ungrouped TikTok Collabs"
                      description="Individual collaborations created outside Unbox, directly on TikTok Shop."
                      placement="top"
                    >
                      <TooltipTrigger
                        className="inline-flex shrink-0 items-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <InfoCircle className="size-3.5 text-quaternary hover:text-tertiary cursor-help transition-colors" />
                      </TooltipTrigger>
                    </Tooltip>
                    {isSmartUnboxGroup && (
                      <span className="inline-flex h-[21px] shrink-0 items-center gap-1 rounded-md border border-[#e9d7fe] bg-[#f9f5ff] pl-1.5 pr-2 py-0.5">
                        <IntersectCircle className="size-3 text-[#6941c6]" />
                        <span className="text-xs font-medium text-[#6941c6]">{item.groupSize} Collabs</span>
                      </span>
                    )}
                  </div>
                  <ProductChips products={isSmartUnboxGroup ? item.groupTopProducts : item.topProducts} />
                </div>

                <div className="flex-1 px-5">
                  <GmvCell value={item.totalGmv} currency={item.totalGmvCurrency} />
                </div>

                <div className="flex-1 px-5">
                  <ModifiedCell
                    updatedAt={item.updatedAt}
                    multiple={isSmartUnboxGroup && item.groupUpdatedAtMultiple}
                  />
                </div>

                <div className="flex-1 px-5">
                  <ExpiryStatusCell status={item.expiryStatus} endTime={item.endTime} />
                </div>

                <div className="flex-1 px-5 text-sm text-tertiary">
                  {invitePendingCount === null ? <MetricSkeleton /> : numberFormatter.format(invitePendingCount)}
                </div>

                <div className="flex-1 px-5 text-sm text-tertiary">
                  {sampleRequestedCount === null ? <MetricSkeleton /> : numberFormatter.format(sampleRequestedCount)}
                </div>

                <div className="flex-1 px-5">
                  <OverdueCell count={postOverdueCount} />
                </div>

                <div className="flex-1 px-5 text-sm text-tertiary">
                  {postedVideosCount === null ? <MetricSkeleton /> : numberFormatter.format(postedVideosCount)}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
